import { useCallback, useEffect, useMemo, useState } from 'react';
import { Server, RefreshCw, KeyRound, CheckCircle2, XCircle, Copy, Download, Search, Clock3, Wifi, WifiOff, Power, PowerOff, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

interface BridgeRegistryTabProps {
  token: string;
}

interface BridgeRow {
  bridge_id: string;
  client_id: string;
  client_name: string;
  mac_address: string;
  is_active: boolean;
  last_seen_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

type SeenState = 'online' | 'recent' | 'stale' | 'never';

const ONLINE_THRESHOLD_MS = 5 * 60 * 1000;
const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

const formatDate = (value: string | null) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleString();
};

const formatRelativeAge = (value: string | null) => {
  if (!value) return 'Never';
  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return 'Unknown';

  const diff = Math.max(0, Date.now() - ts);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const getSeenState = (lastSeenAt: string | null): SeenState => {
  if (!lastSeenAt) return 'never';

  const ts = new Date(lastSeenAt).getTime();
  if (Number.isNaN(ts)) return 'never';

  const diff = Math.max(0, Date.now() - ts);
  if (diff <= ONLINE_THRESHOLD_MS) return 'online';
  if (diff <= STALE_THRESHOLD_MS) return 'recent';
  return 'stale';
};

const getSeenBadgeClass = (state: SeenState) => {
  if (state === 'online') return 'text-emerald-600';
  if (state === 'recent') return 'text-amber-600';
  if (state === 'stale') return 'text-rose-600';
  return 'text-muted-foreground';
};

const getSeenLabel = (state: SeenState) => {
  if (state === 'online') return 'Online';
  if (state === 'recent') return 'Recent';
  if (state === 'stale') return 'Stale';
  return 'Never Seen';
};

const csvEscape = (value: string | number | boolean | null | undefined) => {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  if (!/[",\n]/.test(raw)) return raw;
  return `"${raw.replace(/"/g, '""')}"`;
};

export function BridgeRegistryTab({ token }: BridgeRegistryTabProps) {
  const [bridges, setBridges] = useState<BridgeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reissueLoading, setReissueLoading] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState<string | null>(null);
  const [issuedSecret, setIssuedSecret] = useState<{ bridgeId: string; secret: string } | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'disabled' | SeenState>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const fetchBridges = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch('/api/admin/bridges', { headers });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      const rows: BridgeRow[] = (data.bridges || []).slice();
      rows.sort((a, b) => {
        const left = new Date(b.updated_at || b.last_seen_at || 0).getTime() || 0;
        const right = new Date(a.updated_at || a.last_seen_at || 0).getTime() || 0;
        return left - right;
      });
      setBridges(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bridge registry');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchBridges();
  }, [fetchBridges]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = window.setInterval(() => {
      fetchBridges();
    }, 30000);
    return () => window.clearInterval(id);
  }, [autoRefresh, fetchBridges]);

  const stats = useMemo(() => {
    const active = bridges.filter((bridge) => bridge.is_active).length;
    const disabled = bridges.length - active;
    const online = bridges.filter((bridge) => bridge.is_active && getSeenState(bridge.last_seen_at) === 'online').length;
    const stale = bridges.filter((bridge) => bridge.is_active && getSeenState(bridge.last_seen_at) === 'stale').length;
    return {
      total: bridges.length,
      active,
      disabled,
      online,
      stale,
    };
  }, [bridges]);

  const uniqueClients = useMemo(() => {
    const clientMap = new Map<string, string>();
    bridges.forEach((bridge) => {
      if (bridge.client_id && bridge.client_name) {
        clientMap.set(bridge.client_id, bridge.client_name);
      }
    });
    return Array.from(clientMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ id, name }));
  }, [bridges]);

  const filteredBridges = useMemo(() => {
    const term = search.trim().toLowerCase();

    return bridges.filter((bridge) => {
      const seenState = getSeenState(bridge.last_seen_at);

      if (statusFilter === 'active' && !bridge.is_active) return false;
      if (statusFilter === 'disabled' && bridge.is_active) return false;
      if (statusFilter !== 'all' && statusFilter !== 'active' && statusFilter !== 'disabled' && seenState !== statusFilter) {
        return false;
      }

      if (clientFilter !== 'all' && bridge.client_id !== clientFilter) {
        return false;
      }

      if (!term) return true;

      const haystack = [
        bridge.bridge_id,
        bridge.client_id,
        bridge.client_name,
        bridge.mac_address,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [bridges, search, statusFilter, clientFilter]);

  const copyText = async (value: string, label: string) => {
    try {
      if (!value) return;
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label.toLowerCase()}`);
    }
  };

  const copySecret = async () => {
    if (!issuedSecret?.secret) return;
    await copyText(issuedSecret.secret, 'Bridge secret');
  };

  const exportCsv = () => {
    const rows = filteredBridges;
    if (rows.length === 0) {
      toast.error('No rows to export');
      return;
    }

    const header = [
      'bridge_id',
      'client_id',
      'client_name',
      'mac_address',
      'is_active',
      'seen_state',
      'last_seen_at',
      'updated_at',
      'created_at',
    ];

    const csvLines = [
      header.join(','),
      ...rows.map((bridge) => [
        csvEscape(bridge.bridge_id),
        csvEscape(bridge.client_id),
        csvEscape(bridge.client_name),
        csvEscape(bridge.mac_address),
        csvEscape(bridge.is_active),
        csvEscape(getSeenLabel(getSeenState(bridge.last_seen_at))),
        csvEscape(bridge.last_seen_at),
        csvEscape(bridge.updated_at),
        csvEscape(bridge.created_at),
      ].join(',')),
    ].join('\n');

    const blob = new Blob([csvLines], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `bridge-registry-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    toast.success('Bridge registry CSV exported');
  };

  const reissueSecret = async (bridgeId: string) => {
    const targetBridge = bridges.find((bridge) => bridge.bridge_id === bridgeId);
    if (targetBridge && !targetBridge.is_active) {
      toast.error('Activate this bridge first, then reissue secret');
      return;
    }

    const confirmReissue = window.confirm(`Reissue secret for bridge ${bridgeId}? The old secret will stop working.`);
    if (!confirmReissue) {
      return;
    }

    try {
      setReissueLoading(bridgeId);
      const res = await fetch(`/api/admin/bridges/${encodeURIComponent(bridgeId)}/reissue-secret`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      setIssuedSecret({ bridgeId, secret: data.bridgeSecret || '' });
      await fetchBridges();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reissue bridge secret');
    } finally {
      setReissueLoading(null);
    }
  };

  const toggleBridgeStatus = async (bridge: BridgeRow) => {
    const nextActive = !bridge.is_active;
    const confirmChange = window.confirm(
      nextActive
        ? `Activate bridge ${bridge.bridge_id}?`
        : `Deactivate bridge ${bridge.bridge_id}? Data ingest from this bridge will stop until re-enabled.`
    );

    if (!confirmChange) {
      return;
    }

    try {
      setStatusLoading(bridge.bridge_id);
      const response = await fetch(`/api/admin/bridges/${encodeURIComponent(bridge.bridge_id)}/status`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ is_active: nextActive }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }

      toast.success(`Bridge ${nextActive ? 'activated' : 'deactivated'}`);
      await fetchBridges();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update bridge status');
    } finally {
      setStatusLoading(null);
    }
  };

  const copyBridgeSummary = async (bridge: BridgeRow) => {
    const summary = [
      `Bridge ID: ${bridge.bridge_id}`,
      `Client ID: ${bridge.client_id}`,
      `Client Name: ${bridge.client_name}`,
      `MAC Address: ${bridge.mac_address}`,
      `Status: ${bridge.is_active ? 'Active' : 'Disabled'}`,
      `Connectivity: ${getSeenLabel(getSeenState(bridge.last_seen_at))}`,
      `Last Seen: ${formatDate(bridge.last_seen_at)}`,
      `Updated At: ${formatDate(bridge.updated_at)}`,
    ].join('\n');

    await copyText(summary, 'Bridge details');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <Server className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Bridge Registry</h2>
            <p className="text-sm text-muted-foreground">Admin-only mapping of client, MAC address, and bridge identity</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchBridges} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="rounded-lg border border-border p-3">
          <p className="text-xs text-muted-foreground">Total Bridges</p>
          <p className="text-xl font-semibold">{stats.total}</p>
        </div>
        <div className="rounded-lg border border-border p-3">
          <p className="text-xs text-muted-foreground">Active</p>
          <p className="text-xl font-semibold text-emerald-600">{stats.active}</p>
        </div>
        <div className="rounded-lg border border-border p-3">
          <p className="text-xs text-muted-foreground">Disabled</p>
          <p className="text-xl font-semibold text-rose-600">{stats.disabled}</p>
        </div>
        <div className="rounded-lg border border-border p-3">
          <p className="text-xs text-muted-foreground">Online (5m)</p>
          <p className="text-xl font-semibold text-emerald-600">{stats.online}</p>
        </div>
        <div className="rounded-lg border border-border p-3">
          <p className="text-xs text-muted-foreground">Stale (24h+)</p>
          <p className="text-xl font-semibold text-amber-600">{stats.stale}</p>
        </div>
      </div>

      <div className="rounded-xl border border-border p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-64 flex-1">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by bridge ID, client, or MAC"
              className="pl-9"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as 'all' | 'active' | 'disabled' | SeenState)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
            <option value="online">Online (5m)</option>
            <option value="recent">Recent (&lt; 24h)</option>
            <option value="stale">Stale (24h+)</option>
            <option value="never">Never Seen</option>
          </select>

          {uniqueClients.length > 0 && (
            <select
              value={clientFilter}
              onChange={(event) => setClientFilter(event.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm min-w-[200px]"
            >
              <option value="all">All Clients</option>
              {uniqueClients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          )}

          <Button variant={autoRefresh ? 'default' : 'outline'} onClick={() => setAutoRefresh((value) => !value)} className="gap-2">
            <Clock3 className="h-4 w-4" />
            {autoRefresh ? 'Auto Refresh: ON' : 'Auto Refresh: OFF'}
          </Button>

          <Button variant="outline" onClick={exportCsv} className="gap-2">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          Showing {filteredBridges.length} of {bridges.length} bridge records
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {issuedSecret && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 space-y-2">
          <p className="text-sm font-semibold">New secret issued for {issuedSecret.bridgeId}</p>
          <p className="text-xs text-muted-foreground">This is shown once. Save it in the bridge machine environment immediately.</p>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              readOnly
              value={issuedSecret.secret}
              className="w-full sm:flex-1 sm:min-w-80 px-3 py-2 rounded-md border border-border bg-background text-sm"
            />
            <Button size="sm" variant="outline" onClick={copySecret}>Copy</Button>
            <Button size="sm" variant="ghost" onClick={() => setIssuedSecret(null)}>Clear</Button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/70 text-left sticky top-0">
            <tr>
              <th className="px-3 py-2">Bridge ID</th>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2">MAC Address</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Connectivity</th>
              <th className="px-3 py-2">Last Seen</th>
              <th className="px-3 py-2">Updated</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-3 py-6 text-muted-foreground" colSpan={8}>Loading bridge registry...</td>
              </tr>
            ) : filteredBridges.length === 0 ? (
              <tr>
                <td className="px-3 py-6 text-muted-foreground" colSpan={8}>No bridge credentials found for current filters.</td>
              </tr>
            ) : filteredBridges.map((bridge) => (
              <tr key={bridge.bridge_id} className="border-t border-border/60">
                <td className="px-3 py-2 whitespace-nowrap font-medium">
                  <div className="flex items-center gap-2">
                    <span>{bridge.bridge_id}</span>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={() => copyText(bridge.bridge_id, 'Bridge ID')}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div>{bridge.client_name}</div>
                  <div className="text-xs text-muted-foreground">{bridge.client_id}</div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span>{bridge.mac_address}</span>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={() => copyText(bridge.mac_address, 'MAC address')}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className={`inline-flex items-center gap-1 ${bridge.is_active ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {bridge.is_active ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    {bridge.is_active ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className={`inline-flex items-center gap-1 ${getSeenBadgeClass(getSeenState(bridge.last_seen_at))}`}>
                    {getSeenState(bridge.last_seen_at) === 'online' ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
                    {getSeenLabel(getSeenState(bridge.last_seen_at))}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap" title={formatDate(bridge.last_seen_at)}>{formatRelativeAge(bridge.last_seen_at)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{formatDate(bridge.updated_at)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant={bridge.is_active ? 'outline' : 'default'}
                      className="gap-1"
                      onClick={() => toggleBridgeStatus(bridge)}
                      disabled={statusLoading === bridge.bridge_id}
                    >
                      {bridge.is_active ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                      {statusLoading === bridge.bridge_id
                        ? 'Saving...'
                        : bridge.is_active
                          ? 'Deactivate'
                          : 'Activate'}
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => reissueSecret(bridge.bridge_id)}
                      disabled={reissueLoading === bridge.bridge_id || !bridge.is_active}
                      title={bridge.is_active ? 'Generate a new bridge secret' : 'Activate bridge to reissue secret'}
                    >
                      <KeyRound className="h-3.5 w-3.5" />
                      {reissueLoading === bridge.bridge_id ? 'Reissuing...' : 'Reissue Secret'}
                    </Button>

                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-1"
                      onClick={() => copyBridgeSummary(bridge)}
                      title="Copy bridge details for support"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      Copy Details
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
